import * as Cesium from 'cesium'
import CesiumTools from './CesiumTools'
import dataTools from '../../tools/data.js'

export const GroundStation = {
  data() {
    return {
      ellipsePrimitivesCollection: null,
      groundStationsPC: null,
      groundStationsEllipsesPC: null,
      groundStationsLabelCollection: null,
      groundStationsPointPC: null
    }
  },
  watch: {
    showGroundStations(show) {
      this.groundStationsPC.show = show
    }
  },
  methods: {
    initEllipsePC() {
      this.ellipsePrimitivesCollection = new Cesium.PrimitiveCollection()
      this.viewer.scene.primitives.add(this.ellipsePrimitivesCollection)
    },

    initGroundStationsPC() {
      this.groundStationsPC = new Cesium.PrimitiveCollection()
      this.groundStationsEllipsesPC = new Cesium.PrimitiveCollection()
      this.groundStationsPointPC = new Cesium.PointPrimitiveCollection()
      this.groundStationsLabelCollection = new Cesium.LabelCollection()

      this.groundStationsPC.add(this.groundStationsEllipsesPC)
      this.groundStationsPC.add(this.groundStationsPointPC)
      this.groundStationsPC.add(this.groundStationsLabelCollection)

      this.viewer.scene.primitives.add(this.groundStationsPC)
      this.groundStationsPC.show =
        this.showGroundStations || this.displayOptions.groundStations.show
    },

    addGroundStations(constellationGeometries, groundStationObjects) {
      this.clearGroundStationsPC()
      this.initGroundStationsPC()
      if (groundStationObjects !== null && groundStationObjects.length > 0) {
        groundStationObjects.forEach((groundStation) => {
          const center = Cesium.Cartesian3.fromRadians(
            groundStation.longitude,
            groundStation.latitude
          )
          this.groundStationsPointPC.add({
            position: center,
            pixelSize: 6,
            color: Cesium.Color.WHITE,
            outlineColor: Cesium.Color.BLACK,
            outlineWidth: 1,
            id: groundStation.name
          })
          this.groundStationsLabelCollection.add({
            position: center,
            id: groundStation.name,
            text: groundStation.displayedName
              ? groundStation.displayedName
              : groundStation.name,
            pixelOffset: new Cesium.Cartesian2(15, 10),
            font: '10px sans-serif',
            scaleByDistance: new Cesium.NearFarScalar(1.5e2, 1.5, Infinity, 1),
            // disableDepthTestDistance: Number.POSITIVE_INFINITY,
            style: Cesium.LabelStyle.FILL_AND_OUTLINE
          })
        })
        this.addGroundStationsView(
          constellationGeometries,
          groundStationObjects
        )
      }
    },

    addGroundStationsView(currentGeometries, groundStations) {
      let ellipseInstances = []
      let numberOfColors = currentGeometries.length * groundStations.length
      let geometryIndex = 0
      let groundStationIndex = -1
      currentGeometries.forEach((geometry) => {
        geometryIndex += 1
        groundStations.forEach((groundStation) => {
          groundStationIndex += 1
          const groundStationViewRadius = dataTools.buildStationViewRadius(
            geometry.plane.altitude,
            groundStation.minElevation,
            0
          )
          const center = Cesium.Cartesian3.fromRadians(
            groundStation.longitude,
            groundStation.latitude
          )
          ellipseInstances.push(
            this.createEllipseInstanceFromCenterAndSMAs(
              center,
              groundStationViewRadius,
              groundStationViewRadius,
              numberOfColors,
              geometryIndex,
              groundStationIndex
            )
          )
          ellipseInstances[ellipseInstances.length - 1].id =
            groundStation.name + ' FOV'
        })
      })
      const ellipsePrimitive = new Cesium.GroundPrimitive({
        geometryInstances: ellipseInstances,
        allowPicking: true,
        appearance: new Cesium.PerInstanceColorAppearance({
          flat: true,
          translucent: true
        })
      })
      this.groundStationsEllipsesPC.add(ellipsePrimitive)
    },

    createEllipseInstanceFromCenterAndSMAs(
      center,
      semiMinorAxis,
      semiMajorAxis,
      numberOfColors,
      geometryIndex,
      groundStationIndex
    ) {
      let colorArray = CesiumTools.createColorsArray(numberOfColors)
      let colorsArrayWithTransparency = []
      colorArray.forEach((color) => {
        let colorWithTransparency = Cesium.Color.fromAlpha(color, 0.5)
        colorsArrayWithTransparency.push(colorWithTransparency)
      })

      return new Cesium.GeometryInstance({
        geometry: new Cesium.EllipseGeometry({
          granularity: this.computeGranularity(semiMajorAxis),
          semiMajorAxis: semiMajorAxis,
          semiMinorAxis: semiMinorAxis,
          center: center
        }),
        attributes: {
          color: Cesium.ColorGeometryInstanceAttribute.fromColor(
            colorsArrayWithTransparency[geometryIndex * groundStationIndex]
          ),
          show: new Cesium.ShowGeometryInstanceAttribute(true)
        }
      })
    },

    computeGranularity(semiMajorAxis) {
      // empirical interpolation. There is a bug if granularity is too big, holes appear in the ellipse
      const defaultGranularity = Cesium.Math.RADIANS_PER_DEGREE

      let granularityCoeff = Math.max(
        1,
        1.5 + ((semiMajorAxis - 3e6) * 10) / 6e6
      )
      return defaultGranularity / granularityCoeff
    },

    createEllipsePrimitive(radius, lonlat) {
      CesiumTools.removePrimitivesFromPC(this.ellipsePrimitivesCollection)
      if (isNaN(lonlat.longitude) || isNaN(lonlat.latitude) || !radius) {
        return
      }

      const ellipseInstance = new Cesium.GeometryInstance({
        geometry: new Cesium.EllipseGeometry({
          semiMajorAxis: radius,
          semiMinorAxis: radius,
          center: Cesium.Cartesian3.fromDegrees(
            lonlat.longitude,
            lonlat.latitude
          )
        }),
        attributes: {
          color: new Cesium.ColorGeometryInstanceAttribute(1, 0, 0, 0.5),
          show: new Cesium.ShowGeometryInstanceAttribute(true)
        }
      })

      const ellipsePrimitive = new Cesium.Primitive({
        geometryInstances: ellipseInstance,
        appearance: new Cesium.EllipsoidSurfaceAppearance({
          material: Cesium.Material.fromType('Color')
        })
      })
      this.ellipsePrimitivesCollection.add(ellipsePrimitive)
      this.flyToEllipse(radius, lonlat)
    },

    clearGroundStationsPC() {
      CesiumTools.removePrimitivesFromPC(this.groundStationsPC)
    },

    flyToEllipse(radius, lonlat) {
      if (radius < 250e3) {
        radius = 1e7
      }
      if (this.viewer.scene.mode === Cesium.SceneMode.SCENE3D) {
        const cartoCenter = Cesium.Cartographic.fromDegrees(
          lonlat.longitude,
          lonlat.latitude
        )
        cartoCenter.height = Math.min(radius * 4, 1e7)
        this.viewer.camera.flyTo({
          destination: Cesium.Cartographic.toCartesian(cartoCenter),
          easingFunction: Cesium.EasingFunction.QUADRATIC_IN
        })
      }
    }
  }
}
